import {
  CustomErrorToast,
  CustomSuccessToast,
} from '@/@straico-ui/commons/CustomToasts';
import MediaDetailModal from '@/Components/MediaDetailModal';
import ProfileMenu from '@/Features/App/Components/ProfileMenu';
import useActionsCapabilitiesStore from '@/Features/Core/Components/ActionsCapabilities/store';
import StraicoIcon from '@/Theme/icons/straico';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery as useMediaQueryMUI,
} from '@mui/material';
import { inject, observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';
import GalleryGrid from './components/GalleryGrid';
import TopFilters from './components/TopFilters';
import TopTabs from './components/TopTabs';

const Gallery = inject('store')(
  observer(({ store }) => {
    const [tab, setTab] = useState('all');
    const [loading, setLoading] = useState(true);
    const [media, setMedia] = useState([]);
    const [filters, setFilters] = useState({
      model: [],
      date: null,
      size: [],
      duration: null,
      visibility: null,
    });
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    // eslint-disable-next-line no-unused-vars
    const [limit, setLimit] = useState(20);

    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedMediaItem, setSelectedMediaItem] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Delete modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [mediaToDelete, setMediaToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const isMobile = useMediaQueryMUI((theme) =>
      theme?.breakpoints?.down('md')
    );
    const isSmallMobile = useMediaQueryMUI((theme) =>
      theme?.breakpoints?.down('sm')
    );
    const { theme } = useThemeStore();
    const {
      setGenerationsModelsActive,
      setSelectedModel,
      setSelectedModelParameters,
    } = useActionsCapabilitiesStore();

    useEffect(() => {
      setGenerationsModelsActive(false);
      setSelectedModel(null);
      setSelectedModelParameters(null);
    }, [
      setGenerationsModelsActive,
      setSelectedModel,
      setSelectedModelParameters,
    ]);

    // Effect to handle message parameter from URL
    useEffect(() => {
      const messageId = searchParams.get('message');
      if (messageId) {
        handleOpenMediaFromMessage(messageId);
      }
    }, [searchParams]);

    const handleOpenMediaFromMessage = async (messageId) => {
      try {
        const response = await store.api.get(`/v2/ai/media/${messageId}`);
        if (response.data.status && response.data.data) {
          const mediaItem = response.data.data;
          setSelectedMediaItem(mediaItem);
          setIsDetailModalOpen(true);

          // Remove the message parameter from URL after opening modal
          setSearchParams({});
        } else {
          console.error('Media not found');
        }
      } catch (error) {
        console.error('Error fetching media by message:', error);
      }
    };

    useEffect(() => {
      setPage(1);
      setHasMore(true);
      setMedia([]);
      fetchMedia(tab, 1);
    }, [tab, filters]);

    const fetchMedia = useCallback(
      async (type, page) => {
        setLoading(true);
        try {
          const params = {
            ...(type !== 'all' && { type }),
            ...(filters.model.length > 0 && {
              models: filters.model.map((m) => m._id),
            }),
            ...(filters.date && {
              start_date: filters.date.startDate.toISOString(),
              end_date: filters.date.endDate.toISOString(),
            }),
            ...(filters.size &&
              filters.size.length > 0 && {
                size: filters.size.map((s) => s.value),
              }),
            ...(filters.duration && {
              min_duration: filters.duration.min,
              ...(filters.duration.max !== null && {
                max_duration: filters.duration.max,
              }),
            }),
            ...(filters.visibility && {
              visibility: filters.visibility.value,
            }),
            page,
            limit,
          };

          const response = await store.api.get('/v2/ai/media', { params });

          if (response.data.status) {
            const newMedia = response.data.data;
            setHasMore(newMedia.length === limit);
            setMedia((prevItems) => [...prevItems, ...newMedia]);
          }
        } catch (error) {
          console.error('Error fetching media:', error);
        } finally {
          setLoading(false);
        }
      },
      [filters, limit, store.api]
    );

    const fetchMoreData = async () => {
      if (loading || !hasMore) return;
      const nextPage = page + 1;
      await fetchMedia(tab, nextPage);
      setPage(nextPage);
    };

    const handleFilterChange = useCallback((type, value) => {
      setFilters((prev) => ({
        ...prev,
        [type]: value,
      }));
    }, []);

    const isFilterApplied = useMemo(() => {
      return (
        filters.model.length > 0 ||
        filters.date !== null ||
        filters.size.length > 0 ||
        filters.duration !== null
      );
    }, [filters]);

    const handleMediaClick = useCallback((item) => {
      setSelectedMediaItem(item);
      setIsDetailModalOpen(true);
    }, []);

    const handleCloseDetailModal = useCallback(() => {
      setIsDetailModalOpen(false);
      setSelectedMediaItem(null);
    }, []);

    const handleDeleteMedia = useCallback((mediaItem) => {
      setMediaToDelete(mediaItem);
      setIsDeleteModalOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (!mediaToDelete) return;

      setIsDeleting(true);
      try {
        const response = await store.api.delete(
          `/v2/ai/media/${mediaToDelete._id}`
        );

        if (response.data.status) {
          // Remove from media array
          setMedia((prevMedia) =>
            prevMedia.filter((item) => item._id !== mediaToDelete._id)
          );

          // Close detail modal if deleted item was selected
          if (
            selectedMediaItem &&
            selectedMediaItem._id === mediaToDelete._id
          ) {
            setIsDetailModalOpen(false);
            setSelectedMediaItem(null);
          }

          toast.custom(
            (t) => <CustomSuccessToast message="Media deleted successfully" />,
            { duration: 2000 }
          );
        } else {
          throw new Error(response.data.message || 'Error deleting media');
        }
      } catch (error) {
        console.error('Error deleting media:', error);
        toast.custom(
          (t) => (
            <CustomErrorToast
              message={error.response?.data?.message || 'Error deleting media'}
            />
          ),
          { duration: 2000 }
        );
      } finally {
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
        setMediaToDelete(null);
      }
    }, [mediaToDelete, selectedMediaItem, store.api]);

    const handleCancelDelete = useCallback(() => {
      setIsDeleteModalOpen(false);
      setMediaToDelete(null);
    }, []);

    return (
      <>
        <div
          className={cn('flex flex-col w-full', {
            'bg-seasalt': theme === 'light',
            'bg-lead': theme === 'dark',
            'h-full': !isSmallMobile,
            'h-[calc(100%_-_56px)]': isSmallMobile,
          })}
        >
          {/* Barra superior */}
          <div
            className={cn(
              'relative flex items-center gap-2 flex-col md:flex-row px-2 py-1.5 border-b  h-[57px]',
              {
                'bg-seasalt border-cool-gray/30': theme === 'light',
                'bg-lead border-tornado-cloud': theme === 'dark',
                'justify-center': !isMobile,
                'justify-end': isMobile,
              }
            )}
          >
            <div
              className={cn('absolute', {
                'w-full flex justify-center': isSmallMobile,
                'left-4': !isSmallMobile,
              })}
            >
              <TopTabs tab={tab} setTab={setTab} />
            </div>
            <StraicoIcon className="fill-nue-blue hidden md:block" />
            {!isMobile && (
              <div className="absolute right-4">
                <ProfileMenu />
              </div>
            )}
          </div>
          <div
            className={cn(
              'py-1 md:py-3 m-0 flex flex-1 flex-col !overflow-x-hidden overflow-auto overflow-rtl',
              {
                'overflow-rtl-light': theme === 'light',
                'overflow-rtl-dark': theme === 'dark',
              }
            )}
          >
            {/* Filters */}
            {!isSmallMobile && (
              <div className="sticky top-0 z-10 w-full flex justify-center p-3">
                <TopFilters
                  tab={tab}
                  onFilterChange={handleFilterChange}
                  store={store}
                />
              </div>
            )}
            {/* Galería Masonry sin padding ni margin */}
            <GalleryGrid
              media={media}
              tab={tab}
              loading={loading}
              hasMore={hasMore}
              isFilterApplied={isFilterApplied}
              onMediaClick={handleMediaClick}
              fetchMoreData={fetchMoreData}
              store={store}
            />
          </div>

          {/* Bottom Filters */}
          {isSmallMobile && (
            <div
              className={cn('z-10 w-full flex justify-center p-3', {
                'bg-seasalt': theme === 'light',
                'bg-lead': theme === 'dark',
              })}
            >
              <TopFilters
                tab={tab}
                onFilterChange={handleFilterChange}
                wrap={false}
                store={store}
              />
            </div>
          )}

          <MediaDetailModal
            open={isDetailModalOpen}
            onClose={handleCloseDetailModal}
            mediaItem={selectedMediaItem}
            onDeleteMedia={handleDeleteMedia}
          />

          {/* Delete Confirmation Modal */}
          <Dialog
            open={isDeleteModalOpen}
            onClose={handleCancelDelete}
            PaperProps={{
              style: {
                borderRadius: '21px',
                backgroundColor: theme === 'dark' ? '#252425' : '#F7F7F8',
                width: '90%',
                maxWidth: '400px',
                height: 'auto',
                maxHeight: '90vh',
                padding: '16px',
                overflowY: 'auto',
              },
              elevation: 0,
            }}
          >
            <DialogTitle
              sx={{
                textAlign: 'center',
                color: theme === 'dark' ? '#EFEFEF' : '#211E21',
                fontWeight: 700,
                fontSize: '24px',
                fontFamily: 'Figtree',
                lineHeight: '28px',
                '@media (max-width:600px)': {
                  fontSize: '20px',
                  lineHeight: '24px',
                },
              }}
            >
              Delete Media
            </DialogTitle>
            <DialogContent
              sx={{
                paddingBottom: '1px',
                fontFamily: 'Figtree',
                textAlign: 'center',
                color: theme === 'dark' ? '#EFEFEF' : '#211E21',
                '@media (max-width:600px)': {
                  padding: '12px',
                },
              }}
            >
              Are you sure you want to delete this media? This action cannot be
              undone.
            </DialogContent>
            <DialogActions
              sx={{
                justifyContent: 'center',
                marginTop: '8px',
                fontFamily: 'Figtree',
                gap: '22%',
                '@media (max-width:600px)': {
                  marginTop: '8px',
                  gap: '16px',
                  flexWrap: 'wrap',
                },
              }}
            >
              <Button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                sx={{
                  flex: 1,
                  backgroundColor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  color: theme === 'dark' ? '#7F82C3' : '#5256A6',
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  '&:hover': {
                    backgroundColor: theme === 'dark' ? '#34343E' : '#C7C9DE',
                  },
                  '&:disabled': {
                    backgroundColor: theme === 'dark' ? '#2A2A2A' : '#B0B0B0',
                    color: theme === 'dark' ? '#555555' : '#888888',
                  },
                  '@media (max-width:600px)': {
                    fontSize: '14px',
                    padding: '6px 12px',
                  },
                }}
              >
                <XMarkIcon className="w-[16px] h-[16px]" />
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                sx={{
                  flex: 1,
                  backgroundColor: '#464BBA',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  '&:hover': {
                    backgroundColor: '#3B40A0',
                  },
                  '&:disabled': {
                    backgroundColor: '#B0B0B0',
                    color: '#888888',
                  },
                  '@media (max-width:600px)': {
                    fontSize: '14px',
                    padding: '6px 12px',
                  },
                }}
              >
                <TrashIcon className="w-[16px] h-[16px]" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </>
    );
  })
);

export default Gallery;
